import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Avatar,
  styled,
} from "@mui/material";
import styles from "../RecentReport/Report.module.css";
import NewReportModal from "../NewReport/NewReportModal";
import { useSelector } from "react-redux";
import { useAuthUser } from "react-auth-kit";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import {
  resetDownloadReportSuccess,
  resetSaveReportSuccess,
} from "../../service/reportCreationSlice";
import { useDispatch } from "react-redux";
import { clearGetLatestReports } from "../../../../../service/websocketNotificationsSlice";
import { setToastNotification } from "../../../../../components/ToastNotification/service/toastNotificationSlice";
import getAccessStatus, {
  getSubDomainAccessStatus,
} from "../../../../../components/RolePermissionAccess/RolePermissionAccess";
import { DownloadReportById } from "../../service/reports.request";

const StyledTableCellHead = styled(TableCell)(({ theme }) => ({
  fontFamily: "Poppins",
  lineHeight: "18px",
  backgroundColor: "#073E53",
  color: "#FFFFFF",
  textTransform: "capitalize",
  fontWeight: "400",
  fontSize: 14,
  padding: theme.spacing(1),
}));

const StyledTableCellBody = styled(TableCell)(({ theme }) => ({
  fontFamily: "Poppins",
  fontWeight: "400",
  fontSize: "12px",
  color: "#667085",
  padding: theme.spacing(1),
  paddingTop: "15px",
  paddingBottom: "10px",
}));

const ReportTable = (props: any) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { loading, GetReports, reportData, ViewReports, viewReportSuccess } =
    props;
  const auth = useAuthUser();
  const user = auth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userById } = useSelector((state: any) => state.userDetails);
  const hasReportAllAccess = getAccessStatus(
    "REPORT",
    "ALL_ACCESS",
    userById?.rolePermission
  );
  const hasAllBranchAccess = getSubDomainAccessStatus(
    "GLOBAL_PERMISSIONS",
    "BRANCH_ACCESS",
    "ALL_BRANCHES",
    userById?.rolePermission
  );

  const hasAllCreatedAccess = getSubDomainAccessStatus(
    "REPORT",
    "REPORT_DATA_ACCESS",
    "ALL_REPORTS",
    userById?.rolePermission
  );
  const saveReportSuccess = useSelector(
    (state: any) => state?.reportCreation?.saveReportSuccess
  );
  const { downloadReportSuccess, downloadReportData } = useSelector(
    (state: any) => state?.reportCreation
  );
  const [nameForDownloadedReport, setNameForDownloadedReport] = useState("");
  const { getLatestReports } = useSelector(
    (state: any) => state?.websocketNotifications
  );
  const { downloadAttendanceReportSuccess, downloadAttendanceReportData } =
    useSelector((state: any) => state?.attendanceReportCreation);

  useEffect(() => {
    getReport();
  }, []);

  useEffect(() => {
    if (getLatestReports) {
      dispatch(
        setToastNotification({
          message: "Report Saved Successfully.",
          type: "success",
          snackOpen: true,
        })
      );
      getReport();
      dispatch(clearGetLatestReports());
      dispatch(resetSaveReportSuccess());
    }
  }, [getLatestReports]);

  const getReport = () => {
    type ReportData = {
      instituteId: any;
      isRecentReports: boolean;
      getReports: boolean;
      branchId?: any;
      createdById?: any;
    };
    const data: ReportData = {
      instituteId: user?.institute[0]?.instituteId,
      isRecentReports: true,
      getReports: true,
    };

    if (!hasAllBranchAccess) {
      data.branchId = user?.institute?.[0]?.branchId;
    }
    if (!hasAllCreatedAccess) {
      data.createdById = user?.institute[0]?.id;
    }

    GetReports(data);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const filteredReports =
    reportData && reportData?.length > 0
      ? reportData?.filter(
          (report: any) =>
            report?._source?.name
              ?.toLowerCase()
              ?.includes(searchQuery?.toLowerCase()) ||
            report?._source?.category
              ?.toLowerCase()
              ?.includes(searchQuery?.toLowerCase()) ||
            report?._source?.createdBy
              ?.toLowerCase()
              ?.includes(searchQuery?.toLowerCase())
        )
      : [];

  const base64ToBlob = (base64Data: string, contentType: any) => {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  useEffect(() => {
    if (downloadReportSuccess) {
      const blob = base64ToBlob(
        downloadReportData?.fileBase64String,
        "application/vnd.ms-excel"
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${nameForDownloadedReport}.xlsx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      dispatch(resetDownloadReportSuccess());
      setNameForDownloadedReport("");
    }
  }, [downloadReportSuccess]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{ flexShrink: 0 }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "poppins",
            padding: "16px",
            lineHeight: "24px",
            fontSize: "18px",
            fontWeight: "500",
            paddingLeft: "20px",
            color: "#142C44",
          }}
        >
          Recent Reports
        </Typography>
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          fontFamily={"poppins"}
          fontWeight={400}
        >
          <TextField
            placeholder="Search Reports"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              width: "306px",
              height: "36px",
              borderRadius: "4px",
              border: "1px",
              fontFamily: "poppins",
            }}
          />
          {hasReportAllAccess && <NewReportModal page={"RECENTREPORTS"} />}
        </Box>
      </Box>

      {loading ? (
        <TableSkeleton />
      ) : (
        <>
          {filteredReports?.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "8px",
                border: "1px solid #EAECF0",
                margin: " 0px 18px 0px 18px",
                height: "calc(100vh - 100px)",
                overflowY: "auto",
                boxShadow: "none",
                flexGrow: 1,
                width: "98%",
                maxHeight: "calc(100vh - 220px)",
                minHeight: "calc(100vh - 220px)",
                scrollbarWidth: "none",
              }}
            >
              <Table sx={{ minWidth: 700 }}>
                <TableHead sx={{ background: "#f5f5f5" }}>
                  <TableRow>
                    <StyledTableCellHead
                      sx={{ minWidth: 300, padding: "16px" }}
                    >
                      Reports Name
                    </StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 150 }}>
                      Category
                    </StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 450 }}>
                      Description
                    </StyledTableCellHead>
                    <StyledTableCellHead sx={{ minWidth: 180 }}>
                      Created By
                    </StyledTableCellHead>

                    <StyledTableCellHead sx={{ minWidth: 200 }}>
                      Created On
                    </StyledTableCellHead>
                    <StyledTableCellHead>Actions</StyledTableCellHead>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReports?.map((report: any, index: any) => (
                    <TableRow key={index}>
                      <StyledTableCellBody
                        sx={{
                          color: "#142C44",
                          textTransform: "capitalize",
                          fontSize: "14px",
                        }}
                      >
                        {report?._source?.name}
                      </StyledTableCellBody>
                      <StyledTableCellBody
                        sx={{ textTransform: "capitalize", fontSize: "14px" }}
                      >
                        {report?._source?.category?.toLowerCase()}
                      </StyledTableCellBody>
                      <StyledTableCellBody
                        sx={{ textTransform: "capitalize", fontSize: "14px" }}
                      >
                        {report?._source?.description}
                      </StyledTableCellBody>
                      <StyledTableCellBody
                        sx={{ textTransform: "capitalize", fontSize: "14px" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Box className={styles.userCardImg}>
                            <Avatar
                              sx={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                fontSize: "12px",
                                background: "#EEEEF4",
                                border: "2px solid #FFF",
                                fontFamily: "Poppins",
                                fontWeight: "400",
                                color: "#667085",
                                position: "relative",
                              }}
                            >
                              {report?._source?.createdByName?.charAt(0)}
                            </Avatar>
                          </Box>
                          <Box
                            ml={1}
                            sx={{
                              fontSize: "14px",
                              textTransform: "capitalize",
                            }}
                          >
                            {report?._source?.createdByName}
                          </Box>
                        </Box>
                      </StyledTableCellBody>
                      <StyledTableCellBody
                        sx={{
                          fontSize: "14px",
                        }}
                      >
                        {moment(report?._source?.createdDate).format(
                          "DD MMM YYYY, hh:mm A"
                        )}
                      </StyledTableCellBody>
                      <StyledTableCellBody>
                        <Box display="flex" gap={2}>
                          <div
                            style={{
                              color: "#456DFB",
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                            onClick={() => {
                              navigate(
                                report?._source?.category === "LEAD"
                                  ? "/app/Reports/LeadReportCreation"
                                  : report?._source?.category === "REVENUE"
                                  ? `/app/Reports/Revenue/${report?._source?.id}`
                                  : report?._source?.category === "TRANSACTION"
                                  ? `/app/Reports/Transaction/${report?._source?.id}`
                                  : "/app/Reports/AttendanceReportCreation",
                                {
                                  state: {
                                    from: "RecentReports",
                                    isFromView: true,
                                    reportName: report?._source?.name,
                                    id: report?._source?.id,
                                  },
                                }
                              );
                            }}
                          >
                            View
                          </div>
                          <div
                            style={{
                              color: "#456DFB",
                              cursor: "pointer",
                              fontSize: "14px",
                            }}
                            onClick={() => {
                              if (report?._source?.category === "LEAD") {
                                const flatttenedArray =
                                  report?._source?.reportData?.result.flatMap(
                                    (item: any) => item
                                  );

                                DownloadReportById(report?._source?.id)
                                  .then((res: any) => {
                                    const blob = base64ToBlob(
                                      res?.data?.data?.fileBase64String,
                                      "application/vnd.ms-excel"
                                    );
                                    const url =
                                      window.URL.createObjectURL(blob);
                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.download = `${report?._source?.name}.xlsx`;
                                    document.body.appendChild(link);
                                    link.click();
                                    window.URL.revokeObjectURL(url);
                                    dispatch(
                                      setToastNotification({
                                        message:
                                          "Congratulations! Report is successfully downloaded.",
                                        type: "success",
                                        snackOpen: true,
                                      })
                                    );
                                  })
                                  .catch((err: any) => {});
                              } else {
                                DownloadReportById(report?._source?.id)
                                  .then((res: any) => {
                                    const blob = base64ToBlob(
                                      res?.data?.data?.fileBase64String,
                                      "application/vnd.ms-excel"
                                    );
                                    const url =
                                      window.URL.createObjectURL(blob);
                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.download = `${report?._source?.name}.xlsx`;
                                    document.body.appendChild(link);
                                    link.click();
                                    window.URL.revokeObjectURL(url);
                                    dispatch(
                                      setToastNotification({
                                        message:
                                          "Congratulations! Report is successfully downloaded.",
                                        type: "success",
                                        snackOpen: true,
                                      })
                                    );
                                  })
                                  .catch((err: any) => {});
                              }
                              setNameForDownloadedReport(report?._source?.name);
                            }}
                          >
                            Download
                          </div>
                        </Box>
                      </StyledTableCellBody>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      {filteredReports?.length === 0 &&
        loading === false &&
        searchQuery === "" && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            No Reports Created yet
          </div>
        )}
      {filteredReports?.length === 0 &&
        loading === false &&
        searchQuery !== "" && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            No Data Found
          </div>
        )}
    </Box>
  );
};

export default ReportTable;
